<template>
  <div>
    <div class="d-flex">
      <nav id="breadcrumbs" class="mb-3">
        <ul>
          <li>
            <router-link :to="{ name: 'company.dashboard' }">
              <i class="uil-home-alt"></i>
            </router-link>
          </li>
          <li>
            <span>{{ $t("report.company_dashboard.reports") }}</span>
          </li>
          <li>{{ $t("report.analytics") }}</li>
        </ul>
      </nav>
    </div>

    <div class="section-header mb-lg-2 border-0 uk-flex-middle">
      <div class="section-header-left">
        <h2 class="uk-heading-line text-left">
          <span>{{ $t("report.analytics") }}</span>
        </h2>
      </div>
    </div>

    <div class="card metabase-report-card">
      <div ref="cardBody" class="card-body metabase-report-card-body">
        <default-loading v-if="loading" />

        <div v-else-if="error" class="uk-alert-danger" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p class="mb-0">{{ error }}</p>
        </div>

        <div v-else-if="iframeUrl" class="metabase-report-frame-wrapper" style="height: 4200px !important;">
          <iframe
              style="height: 4200px !important;"
            ref="reportFrame"
            :src="iframeUrl"
            class="metabase-report-frame"
            frameborder="0"
            allowtransparency
            @load="handleIframeLoad"
          />
        </div>

        <div v-else class="uk-alert-warning" uk-alert>
          <a class="uk-alert-close" uk-close></a>
          <p class="mb-0">{{ $t('general.report_link_not_found') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import DefaultLoading from "@/view/components/loading/DefaultLoading";

const REPORT_BASE_URL =
  "https://metabase.anzera.com.tr/public/dashboard/57051d6b-2696-44ab-b3a9-d86555afd0fb?hash=";

export default {
  name: "MetabaseReport",
  components: {
    DefaultLoading,
  },
  data() {
    return {
      loading: true,
      error: null,
      iframeHeight: 600,
      reportHash: null,
      resizeObserver: null,
      recalcTimeouts: [],
    };
  },
  computed: {
    iframeUrl() {
      if (!this.reportHash) {
        return null;
      }

      return `${REPORT_BASE_URL}${encodeURIComponent(this.reportHash)}`;
    },
  },
  methods: {
    clearRecalcTimers() {
      this.recalcTimeouts.forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      this.recalcTimeouts = [];
    },
    updateIframeHeight() {
      this.$nextTick(() => {
        const cardBody = this.$refs.cardBody;

        if (!cardBody) {
          return;
        }

        const rect = cardBody.getBoundingClientRect();
        const viewportHeight = document.documentElement.clientHeight;
        const availableHeight = viewportHeight - rect.top - 16;

        this.iframeHeight = Math.max(availableHeight, 600);
      });
    },
    scheduleHeightRecalc() {
      this.clearRecalcTimers();

      [0, 150, 400, 800, 1500].forEach((delay) => {
        const timeoutId = setTimeout(() => {
          this.updateIframeHeight();
        }, delay);

        this.recalcTimeouts.push(timeoutId);
      });
    },
    handleIframeLoad() {
      this.scheduleHeightRecalc();
    },
    async fetchReportHash() {
      this.loading = true;
      this.error = null;

      try {
        const response = await ApiService.get("api/company/self");
        this.reportHash = response?.data?.report_hash || null;

        if (!this.reportHash) {
          this.error = this.$t("general.report_hash_not_found");
        }
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          this.$t("general.report_info_fetch_error");
      } finally {
        this.loading = false;
        this.scheduleHeightRecalc();
      }
    },
  },
  mounted() {
    this.scheduleHeightRecalc();
    this.fetchReportHash();
    window.addEventListener("resize", this.updateIframeHeight);
    window.addEventListener("orientationchange", this.updateIframeHeight);

    if (window.ResizeObserver && this.$refs.cardBody) {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateIframeHeight();
      });
      this.resizeObserver.observe(this.$refs.cardBody);
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateIframeHeight);
    window.removeEventListener("orientationchange", this.updateIframeHeight);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.clearRecalcTimers();
  },
};
</script>

<style scoped>
.metabase-report-card-body {
  padding: 0;
}

.metabase-report-card {
  width: 100%;
  max-width: none;
  overflow: hidden;
}

.metabase-report-frame-wrapper {
  width: 100%;
}

.metabase-report-frame {
  width: 100%;
  border: 0;
  display: block;
}
</style>
